import { Component, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DropdownModule } from 'primeng/dropdown';
import { CheckboxModule } from 'primeng/checkbox';
import { MessagesModule } from 'primeng/messages';
import { LucideIconsModule } from '@shared/lucide-icons.module';
import { DividerModule } from 'primeng/divider';
import { InspectorBase } from '@shared/components/joint-js/components/inspectors/inspector-base';
import { StartChatConversationNode } from '@modules/campaigns/Domain/Nodes/StartChatConversationNode';
import { StartChatConversationConfig } from '@modules/campaigns/Domain/Interfaces/INodeConfig';
import { WorkflowStateService } from '@shared/state/workflow-state.service';
import { ChatAgentsService } from '@shared/components/joint-js/services/chat-agents.service';
import { getValidationErrorMessage } from '@modules/campaigns/Domain/Enums/ValidationErrors';

@Component({
  selector: 'app-start-chat-conversation-inspector',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    DropdownModule,
    CheckboxModule,
    MessagesModule,
    LucideIconsModule,
    DividerModule,
  ],
  templateUrl: './start-chat-conversation-inspector.component.html',
  styleUrls: ['./start-chat-conversation-inspector.component.scss'],
})
export class StartChatConversationInspectorComponent extends InspectorBase<StartChatConversationNode> {
  agents = signal<any[]>([]);
  selectedAgentId = signal<string | null>(null);
  allContacts = signal<boolean>(false);

  constructor(
    private workflowState: WorkflowStateService,
    private chatAgentsService: ChatAgentsService,
    private translate: TranslateService,
  ) {
    super();
    this.loadAgents();
  }

  get nodeValue(): StartChatConversationNode | null {
    return this.node() as StartChatConversationNode;
  }

  getTabs(): { label: string; value: 'preview' | 'config' }[] {
    return [
      {
        label: this.translate.instant(
          'DATA.MODULES.INSPECTORS.START_CHAT_CONVERSATION.TABS.PREVIEW',
        ),
        value: 'preview',
      },
      {
        label: this.translate.instant(
          'DATA.MODULES.INSPECTORS.START_CHAT_CONVERSATION.TABS.CONFIG',
        ),
        value: 'config',
      },
    ];
  }

  protected refreshFromConfig(): void {
    const config = this.nodeValue?.config as StartChatConversationConfig;
    if (!config) return;

    this.selectedAgentId.set(config.agentId || null);
    this.allContacts.set(config.allContacts ?? false);
  }

  private loadAgents(): void {
    this.chatAgentsService.searchChatAgents().subscribe((agents) => {
      this.agents.set(agents);
    });
  }

  get selectedAgent(): any {
    return this.agents().find((a) => a.id === this.selectedAgentId()) ?? null;
  }

  onAgentChange(agentId: string): void {
    this.selectedAgentId.set(agentId);
    this.applyChanges();
  }

  onAllContactsChange(): void {
    this.applyChanges();
  }

  applyChanges(): void {
    const node = this.nodeValue;
    if (!node) return;

    const updatedConfig: StartChatConversationConfig = {
      ...node.config,
      agentId: this.selectedAgentId() ?? '',
      allContacts: this.allContacts(),
    };

    const updatedNode = new StartChatConversationNode(node.id, node.position, updatedConfig);
    updatedNode.parentNodes = [...node.parentNodes];
    updatedNode.childrenNodes = [...node.childrenNodes];

    const validationErrors = updatedNode.validate();
    this.errors.set(validationErrors);

    if (validationErrors.length > 0) return;

    this.workflowState.addOrUpdateNode(updatedNode);
  }

  getErrorMessage(error: any): string {
    return getValidationErrorMessage(error.code || error.errorMessage, error.params, this.translate);
  }
}
